import { all, put, call, takeEvery, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { IRequestParams, request } from 'helpers/request';
import { downloadFile, getFileNameFromContentDisposition } from 'helpers';
import { logSagaErrorMessage } from 'utils';
import { ENDPOINTS } from 'utils/constants';
import * as types from 'modules/admin-panel/pages/results/ActionTypes';
import {
	getBusinessUnitList,
	getCompareTrendsList,
	getDashboardComments,
	getDashboardCommentsWordcloud,
	getDashboardContent,
	getDashboardRegions,
	getDashboardReport,
	getDashboardServicesCards,
	getMapData,
	getNpsTrends,
	getQuickQuestionAnswers,
	getQuickQuestionAnswersWordcloud,
	getRangeData,
	getRolesList,
	getRoundData,
	getRoundsList,
	getServicesList,
	getStatistic,
	getSurveyResults,
	getTrendsList,
	setActiveDashboardAnswersType,
	setActiveDashboardCommentType,
	setActiveRole,
	getServiceStats,
} from 'modules/admin-panel/pages/results/results.actions';
import { getPersonaListSaga } from './persona.saga';
import {
	endpointsFactory,
	getAllDataForRoundSagaFactory,
	getAllDataSagaFactory,
	getAndSetRolesSagaFactory,
	getContentDataSagaFactory,
	getDataWithGridStatisticFactory,
	getRolesAndContentDataSagaFactory,
} from './helpers/sagaContainers';
import { IDashboardReportPayload } from '../models';

const {
	getCommentsSaga,
	getCommentsWordcloudSaga,
	getServiceStatsSaga,
	getServiceCardsSaga,
	getMapPointsSaga,
	getTrendsSaga,
	getCompareTrendsSaga,
	getRoundsSaga,
	getServicesSaga,
	getRolesSaga,
	getBusinessUnitsSaga,
	getRangeDataSaga,
	getNpsResultsSaga,
	getQuickQuestionAnswersSaga,
	getQuickQuestionAnswersWordcloudSaga,
	getStatisticSaga,
	getRegionsSaga,
} = endpointsFactory({
	getCommentsAction: getDashboardComments,
	getWordcloudAction: getDashboardCommentsWordcloud,
	getServicesAction: getServicesList,
	getServiceCardsAction: getDashboardServicesCards,
	getPointsAction: getMapData,
	getRoundsAction: getRoundsList,
	getTrendsAction: getTrendsList,
	getCompareTrendsAction: getCompareTrendsList,
	getRolesAction: getRolesList,
	getBusinessUnitsAction: getBusinessUnitList,
	getRangeDataAction: getRangeData,
	getNpsResultsAction: getNpsTrends,
	getQuestionAnswersAction: getQuickQuestionAnswers,
	getQuestionAnswersWordcloudAction: getQuickQuestionAnswersWordcloud,
	getStatisticAction: getStatistic,
	getRegionsAction: getDashboardRegions,
	getServiceStatsAction: getServiceStats,
});

const getContentDataSaga = getContentDataSagaFactory({
	getTrendsSaga,
	getMapPointsSaga,
	getRegionsSaga,
	getBusinessUnitsSaga,
	getServiceCardsSaga,
	getServiceStatsSaga, // for Service Page
	setActiveCommentTypeAction: setActiveDashboardCommentType,
	setActiveQuickAnswersAction: setActiveDashboardAnswersType, // only for Dashboard page
	getContentAction: getDashboardContent,
});

const getContentDataWithStatisticSaga = getDataWithGridStatisticFactory({
	getDataSaga: getContentDataSaga,
	getStatisticSaga,
});

const getAllDataForNewRoundSaga = getAllDataForRoundSagaFactory({
	getServicesSaga,
	getRegionsSaga,
	getMapPointsSaga,
	getRolesSaga,
	getBusinessUnitsSaga,
	getStatisticSaga,
	getPersonaListSaga, // only for Dashboard page
	getRoundDataSaga: getContentDataSaga,
	getRoundDataAction: getRoundData,
});

const getSurveyResultsSaga = getAllDataSagaFactory({
	getRoundsSaga,
	getServicesSaga,
	getRolesSaga,
	getStatisticSaga,
	getContentDataSaga,
	getPersonaListSaga, // only for Dashboard page
	getAllDataAction: getSurveyResults,
	getRangeDataSaga,
});

const getAndSetRolesSaga = getAndSetRolesSagaFactory({
	getRolesSaga,
	setActiveRoleAction: setActiveRole,
});

const getRolesDataSaga = getRolesAndContentDataSagaFactory({
	getAndSetRolesSaga,
	getContentDataSaga,
	getContentAction: getDashboardContent,
});

const getRolesAndContentDataSaga = getDataWithGridStatisticFactory({
	getDataSaga: getRolesDataSaga,
	getStatisticSaga,
});

function* getDashboardReportSaga({
	payload,
}: {
	type: string;
	payload: IDashboardReportPayload;
}) {
	try {
		yield put(getDashboardReport.loading());

		const getDashboardReportParams: IRequestParams = {
			url: ENDPOINTS.dashboardReport,
			params: {
				...payload,
				businessUnit: encodeURIComponent(payload.businessUnit || ''),
			},
		};

		const { data, headers }: AxiosResponse<File> = yield call(
			request,
			getDashboardReportParams,
		);
		const fileName = getFileNameFromContentDisposition(
			headers['content-disposition'],
		);
		downloadFile(URL.createObjectURL(new Blob([data])), fileName);

		yield put(getDashboardReport.success());
	} catch (e) {
		logSagaErrorMessage('get dashboard report', e);
		yield put(getDashboardReport.fail(e));
	}
}

export function* dashboardSaga() {
	yield all([
		takeEvery(types.GET_DASHBOARD_COMMENTS.DEFAULT, getCommentsSaga),
		takeEvery(
			types.GET_DASHBOARD_COMMENTS_WORDCLOUD.DEFAULT,
			getCommentsWordcloudSaga,
		),
		takeLatest(types.GET_SURVEY_RESULTS.DEFAULT, getSurveyResultsSaga),
		takeLatest(types.SET_DASHBOARD_ACTIVE_ROUND, getAllDataForNewRoundSaga),
		takeLatest(
			[
				types.SET_DASHBOARD_ACTIVE_LOCATION,
				types.SET_DASHBOARD_ACTIVE_BUSINESS_UNIT,
			],
			getRolesAndContentDataSaga,
		),
		takeLatest(
			[types.SET_DASHBOARD_ACTIVE_SERVICE, types.SET_DASHBOARD_ACTIVE_ROLE],
			getContentDataWithStatisticSaga,
		),
		takeEvery(types.GET_DASHBOARD_REPORT.DEFAULT, getDashboardReportSaga),
		takeLatest(
			types.GET_DASHBOARD_COMPARE_TRENDS_LIST.DEFAULT,
			getCompareTrendsSaga,
		),
		takeLatest(types.GET_NPS_TRENDS.DEFAULT, getNpsResultsSaga),
		takeEvery(
			types.GET_QUICK_QUESTION_ANSWERS.DEFAULT,
			getQuickQuestionAnswersSaga,
		),
		takeEvery(
			types.GET_QUICK_QUESTION_ANSWERS_WORDCLOUD.DEFAULT,
			getQuickQuestionAnswersWordcloudSaga,
		),
		takeEvery(types.GET_DASHBOARD_SERVICE_CARDS.DEFAULT, getServiceCardsSaga),
		// @ts-ignore
		takeEvery(types.GET_STATISTIC.DEFAULT, getStatisticSaga),
	]);
}
